import {
  FRACTION_ONE,
  FRACTION_TWELVE,
  FRACTION_TWO,
} from "@aprao/common/src/math/fraction.js";
import { combineNullables } from "@aprao/common/src/util/combineNullables.js";
import { makeAutoObservable } from "mobx";
import { mapForNullable } from "option-t/nullable";

import type { ShapeInterestLender } from "../../shapes/ShapeLender.js";
import { InterestFacade } from "./InterestFacade.js";
import {
  LenderBaseFacade,
  type LenderBaseFacadeProviders,
  type LenderFeeFacade,
} from "./LenderBaseFacade.js";

export const computeInterestMonthlyRatio = (lender: InterestLenderFacade) =>
  mapForNullable(lender.interest.rate.rateMargin, (rate) =>
    rate.div(
      lender.interest.rate.ratePeriod === "year" ?
        FRACTION_TWELVE
      : FRACTION_ONE,
    ),
  );

export class InterestLenderFacade {
  readonly loanType = "interest";

  readonly $model;

  readonly _lenderBase;

  readonly _providers;

  constructor(
    model: ShapeInterestLender,
    providers: LenderBaseFacadeProviders,
  ) {
    makeAutoObservable(this);

    this.$model = model;
    this._providers = providers;
    this._lenderBase = new LenderBaseFacade(this.$model, providers);
  }

  get name() {
    return this._lenderBase.name;
  }

  get facilitySize() {
    return this._lenderBase.facilitySize;
  }

  get fees() {
    return this._lenderBase.fees;
  }

  get definedTermInMonths() {
    return this.$model.definedTermInMonths;
  }

  get interest() {
    return new InterestFacade(this.$model.interest);
  }

  get estimatedInterest() {
    return combineNullables(
      (facilitySize, monthlyRatio, durationInMonth) => {
        const ratio = monthlyRatio
          .div(
            // estimated on drawn balance is just half of on full amount
            this.interest.formulation === "drawnBalance" ?
              FRACTION_TWO
            : FRACTION_ONE,
          )
          .mul(durationInMonth);

        if (this.interest.payoutType === "retained") {
          return this.interest.definedAllowance == null ?
              // when allowance is not given we consider the interest = allowance
              ratio.mul(facilitySize.div(FRACTION_ONE.add(ratio)))
            : ratio.mul(facilitySize.sub(this.interest.definedAllowance));
        } else {
          return ratio.mul(facilitySize);
        }
      },
      this.facilitySize.relativesSet.getRelative("absolute"),
      computeInterestMonthlyRatio(this),
      this.definedTermInMonths,
    );
  }

  get interestAllowance() {
    return this.interest.payoutType === "retained" ?
        this.interest.definedAllowance
      : null;
  }

  set name(name: string) {
    this._lenderBase.name = name;
  }

  set definedTermInMonths(term: number | null) {
    this.$model.definedTermInMonths = term;
  }

  addFee() {
    this._lenderBase.addFee();
  }

  removeFee(fee: LenderFeeFacade) {
    this._lenderBase.removeFee(fee);
  }
}
